import PropTypes from "prop-types";
import React from "react";
import { Redirect, Route } from "react-router-dom";
import { getCookies, scopesCookieKey } from "../API/cookies";

/**
 * This component is a wrapper for the more general Route component from react-router-dom
 * It works just like the Route component, however it takes an additional prop of "permission"
 * that can be a string, array or object.
 * If it's a string it simply checks that the user's sp_access_token_scope cookie includes the string.
 * If it's an array, all strings in array must be included in the sp_access_token_scope
 * If it's an object, the object key will be checked against the url to see if it is present,
 * if it is, then we check to see if the permission is in scope. This feature allows us to use this
 * component for only specific url params, which allows us to permission tabs and modals individually.
 */

const PermissionedRoute = ({ children, permission, ...rest }) => {
  const scope = getCookies(scopesCookieKey);
  const permissions = scope ? scope.split(" ") : [];
  permissions.push(null); // allow for null permissions in arrays/objects

  const determinePermissions = (location) => {
    if (!permission) return true;
    if (typeof permission === "string") return permissions.includes(permission);
    if (Array.isArray(permission)) return permission.every((p) => permissions.includes(p));
    if (typeof permission === "object") {
      return Object.entries(permission).every((kv) => {
        if (location.pathname.includes(kv[0])) return permissions.includes(kv[1]);
        return true;
      });
    }
    return false;
  };

  return (
    <Route
      {...rest}
      render={({ location }) =>
        determinePermissions(location) ? (
          <>{children}</>
        ) : (
          <Redirect
            to={{
              pathname: "/",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

PermissionedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  permission: PropTypes.any,
};

PermissionedRoute.defaultProps = {
  permission: null,
};

export default PermissionedRoute;
